import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, IconButton, Chip,
  InputAdornment, Alert, Snackbar, Tooltip, LinearProgress, MenuItem,
} from '@mui/material';
import {
  Add, Edit, Delete, Search, TrendingUp, TrendingDown,
  ColorLens, Warning, Archive, Unarchive, SwapHoriz, History,
} from '@mui/icons-material';
import { getAllFabrics, createFabric, updateFabric, deleteFabric, addMaterialMovement, getFabricUsage } from '../api';

// Принимает любые цифры/точки/запятые независимо от раскладки клавиатуры.
// Возвращает строку с одной точкой как разделителем.
const normalizeDecimalInput = (raw) => {
  if (raw === '' || raw == null) return '';
  // Удаляем всё, кроме цифр, точки и запятой
  let cleaned = String(raw).replace(/[^0-9.,]/g, '');
  // Запятую -> точку
  cleaned = cleaned.replace(/,/g, '.');
  // Оставляем только первую точку
  const firstDot = cleaned.indexOf('.');
  if (firstDot !== -1) {
    cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
  }
  return cleaned;
};

export default function FabricManagement({ user }) {
  const [fabrics, setFabrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const [fabricDialog, setFabricDialog] = useState(false);
  const [movementDialog, setMovementDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [usageDialog, setUsageDialog] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const [selectedFabric, setSelectedFabric] = useState(null);

  const [fabricForm, setFabricForm] = useState({
    name: '', quantity: '', unit: 'м', price_per_unit: '',
  });

  const [movementForm, setMovementForm] = useState({
    quantity: '', movement_type: 'income', reason: '',
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadFabrics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const loadFabrics = async () => {
    try {
      setLoading(true);
      const response = await getAllFabrics({ include_archived: showArchived });
      setFabrics(response.data);
    } catch (error) {
      showSnackbar('Ошибка загрузки тканей', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredFabrics = fabrics.filter(fabric =>
    fabric.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Ткани на исходе (quantity < 10 и не в архиве)
  const lowStockFabrics = fabrics.filter(f => f.quantity < 10 && !f.is_archived);

  const openFabricDialog = (fabric = null) => {
    if (fabric) {
      setFabricForm({
        name: fabric.name,
        quantity: fabric.quantity,
        unit: fabric.unit,
        price_per_unit: fabric.price_per_unit || '',
      });
      setSelectedFabric(fabric);
    } else {
      setFabricForm({ name: '', quantity: '', unit: 'м', price_per_unit: '' });
      setSelectedFabric(null);
    }
    setFabricDialog(true);
  };

  const handleSaveFabric = async () => {
    try {
      if (!fabricForm.name.trim()) {
        showSnackbar('Введите название ткани', 'error');
        return;
      }
      if (selectedFabric) {
        await updateFabric(selectedFabric.id, {
          price_per_unit: parseFloat(fabricForm.price_per_unit) || 0,
        });
        showSnackbar('Ткань обновлена');
      } else {
        await createFabric({
          ...fabricForm,
          quantity: 0,
          price_per_unit: parseFloat(fabricForm.price_per_unit) || 0,
        });
        showSnackbar('Ткань добавлена');
      }
      setFabricDialog(false);
      loadFabrics();
    } catch (error) {
      const message = error.response?.data?.detail || 'Ошибка сохранения';
      showSnackbar(message, 'error');
    }
  };

  const openMovementDialog = (fabric, type = 'income') => {
    setSelectedFabric(fabric);
    setMovementForm({ quantity: '', movement_type: type, reason: '' });
    setMovementDialog(true);
    // При расходе показываем куда уже использовалась ткань
    if (type === 'expense') {
      setUsageLoading(true);
      getFabricUsage(fabric.id).then(res => {
        setUsageData(res.data);
      }).catch((err) => {
        console.warn('Failed to load fabric usage:', err);
        setUsageData(null);
      }).finally(() => setUsageLoading(false));
    } else {
      setUsageData(null);
    }
  };

  const handleSaveMovement = async () => {
    try {
      if (!movementForm.quantity || parseFloat(movementForm.quantity) <= 0) {
        showSnackbar('Введите корректное количество', 'error');
        return;
      }
      await addMaterialMovement({
        fabric_id: selectedFabric.id,
        quantity: parseFloat(movementForm.quantity),
        movement_type: movementForm.movement_type,
        reason: movementForm.reason,
      });
      const typeLabel = movementForm.movement_type === 'income' ? 'Приход' : 'Расход';
      showSnackbar(`${typeLabel} добавлен`);
      setMovementDialog(false);
      loadFabrics();
    } catch (error) {
      const message = error.response?.data?.detail || 'Ошибка операции';
      showSnackbar(message, 'error');
    }
  };

  const handleArchiveFabric = async (fabric) => {
    try {
      await updateFabric(fabric.id, { is_archived: !fabric.is_archived });
      showSnackbar(fabric.is_archived ? 'Ткань восстановлена из архива' : 'Ткань перемещена в архив');
      loadFabrics();
    } catch (error) {
      showSnackbar('Ошибка', 'error');
    }
  };

  const handleDeleteFabric = async () => {
    try {
      await deleteFabric(selectedFabric.id);
      showSnackbar('Ткань удалена');
      setDeleteDialog(false);
      loadFabrics();
    } catch (error) {
      showSnackbar(error.response?.data?.detail || 'Ошибка удаления', 'error');
    }
  };

  const openUsageDialog = async (fabric) => {
    setSelectedFabric(fabric);
    setUsageDialog(true);
    setUsageLoading(true);
    try {
      const response = await getFabricUsage(fabric.id);
      setUsageData(response.data);
    } catch (error) {
      showSnackbar('Ошибка загрузки истории', 'error');
    } finally {
      setUsageLoading(false);
    }
  };

  return (
    <Box>
      {/* Заголовок */}
      <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }} gap={2} mb={3}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ColorLens sx={{ fontSize: 36, color: '#1e40af' }} />
            Ткани в наличии
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Управление каталогом тканей и их остатками
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant={showArchived ? 'contained' : 'outlined'}
            size="small"
            startIcon={<Archive />}
            onClick={() => setShowArchived(!showArchived)}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {showArchived ? 'Скрыть архив' : 'Показать архив'}
          </Button>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => openFabricDialog()}
              sx={{
                borderRadius: 2, px: 3, fontWeight: 600, textTransform: 'none',
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
              }}
            >
              Добавить ткань
            </Button>
          )}
        </Box>
      </Box>

      {/* Предупреждение о тканях на исходе */}
      {lowStockFabrics.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }} icon={<Warning />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Ткани на исходе ({lowStockFabrics.length}):
          </Typography>
          <Typography variant="body2">
            {lowStockFabrics.map(f => `${f.name} — ${Math.round(f.quantity * 100) / 100} ${f.unit}`).join(' | ')}
          </Typography>
        </Alert>
      )}

      {/* Поиск */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Поиск по названию..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search sx={{ color: '#64748b' }} /></InputAdornment>,
          }}
        />
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          Найдено: {filteredFabrics.length}
        </Typography>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* Таблица тканей */}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 600 }}>Название</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Остаток</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Ед.</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Цена закупки</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Статус</TableCell>
              {isAdmin && <TableCell sx={{ fontWeight: 600 }} align="center">Действия</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFabrics.map((fabric) => {
              const isLow = fabric.quantity < 10 && fabric.quantity > 0;
              const isEmpty = fabric.quantity <= 0;

              return (
                <TableRow
                  key={fabric.id}
                  hover
                  sx={{
                    opacity: fabric.is_archived ? 0.5 : 1,
                    bgcolor: isEmpty ? '#fef2f2' : isLow ? '#fffbeb' : 'inherit',
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {fabric.name}
                      </Typography>
                      {fabric.is_archived && (
                        <Chip label="Архив" size="small" variant="outlined" color="default" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 700, color: isEmpty ? '#dc2626' : isLow ? '#d97706' : '#059669' }}>
                      {Math.round(fabric.quantity * 100) / 100}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="textSecondary">{fabric.unit}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {fabric.price_per_unit > 0 ? `${fabric.price_per_unit.toLocaleString()} ₸` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {isEmpty ? (
                      <Chip label="Закончилась" size="small" color="error" />
                    ) : isLow ? (
                      <Chip label="Мало" size="small" color="warning" />
                    ) : (
                      <Chip label="В наличии" size="small" color="success" />
                    )}
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={0.5}>
                        <Tooltip title="История использования">
                          <IconButton aria-label={`История использования ткани ${fabric.name}`} size="small" color="info" onClick={() => openUsageDialog(fabric)}>
                            <History fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Приход">
                          <IconButton aria-label={`Добавить приход ткани ${fabric.name}`} size="small" color="success" onClick={() => openMovementDialog(fabric, 'income')}>
                            <TrendingUp fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Расход">
                          <IconButton aria-label={`Списать ткань ${fabric.name}`} size="small" color="warning" onClick={() => openMovementDialog(fabric, 'expense')}>
                            <TrendingDown fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Редактировать">
                          <IconButton aria-label={`Редактировать ткань ${fabric.name}`} size="small" onClick={() => openFabricDialog(fabric)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={fabric.is_archived ? 'Из архива' : 'В архив'}>
                          <IconButton aria-label={`${fabric.is_archived ? 'Восстановить' : 'Архивировать'} ткань ${fabric.name}`} size="small" onClick={() => handleArchiveFabric(fabric)}>
                            {fabric.is_archived ? <Unarchive fontSize="small" /> : <Archive fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить">
                          <IconButton aria-label={`Удалить ткань ${fabric.name}`} size="small" color="error" onClick={() => { setSelectedFabric(fabric); setDeleteDialog(true); }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {!loading && filteredFabrics.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    {searchQuery ? 'Ткани не найдены' : 'Нет добавленных тканей'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Диалог создания/редактирования */}
      <Dialog open={fabricDialog} onClose={() => setFabricDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ColorLens sx={{ color: '#1e40af' }} />
          {selectedFabric ? 'Редактировать ткань' : 'Новый вид ткани'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth margin="normal" label="Название ткани"
            placeholder="Например: Велюр синий"
            value={fabricForm.name}
            onChange={(e) => setFabricForm({ ...fabricForm, name: e.target.value })}
            disabled={!!selectedFabric}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Box display="flex" gap={2} mt={1}>
            <TextField
              fullWidth label="Количество"
              value={selectedFabric ? fabricForm.quantity : 0}
              disabled
              helperText="Используйте приход или расход после создания"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              select label="Единица" value={fabricForm.unit}
              onChange={(e) => setFabricForm({ ...fabricForm, unit: e.target.value })}
              sx={{ minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="м">метры (м)</MenuItem>
              <MenuItem value="м²">кв.метры (м²)</MenuItem>
              <MenuItem value="шт">штуки (шт)</MenuItem>
              <MenuItem value="рул">рулоны (рул)</MenuItem>
            </TextField>
          </Box>
          <TextField
            fullWidth margin="normal" type="text"
            label="Закупочная цена за единицу (₸)"
            placeholder="0"
            value={fabricForm.price_per_unit}
            onChange={(e) => setFabricForm({ ...fabricForm, price_per_unit: normalizeDecimalInput(e.target.value) })}
            inputProps={{ inputMode: 'decimal', pattern: '[0-9.,]*' }}
            helperText="Цена за метр/штуку при закупке у поставщика"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setFabricDialog(false)} sx={{ borderRadius: 2, textTransform: 'none', color: '#64748b' }}>
            Отмена
          </Button>
          <Button onClick={handleSaveFabric} variant="contained"
            sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' }}>
            {selectedFabric ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог движения */}
      <Dialog open={movementDialog} onClose={() => setMovementDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHoriz sx={{ color: movementForm.movement_type === 'income' ? '#059669' : '#d97706' }} />
          {movementForm.movement_type === 'income' ? 'Добавить приход' : 'Списать расход'}
        </DialogTitle>
        <DialogContent>
          {selectedFabric && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              <Typography variant="body2">Ткань: <strong>{selectedFabric.name}</strong></Typography>
              <Typography variant="body2">Текущий остаток: <strong>{Math.round(selectedFabric.quantity * 100) / 100} {selectedFabric.unit}</strong></Typography>
            </Alert>
          )}
          {movementForm.movement_type === 'expense' && usageData && usageData.sofas && usageData.sofas.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Куда уже использовалась ткань ({usageData.sofas.length}):
              </Typography>
              {usageData.sofas.slice(0, 8).map((s, i) => (
                <Typography key={i} variant="caption" display="block" color="text.secondary">
                  • Диван #{s.sofa_number} — {s.model_name || '—'} ({s.fabric_type}, {s.consumption} {usageData.unit}) — {s.created_at ? new Date(s.created_at).toLocaleDateString('ru-RU') : ''}
                </Typography>
              ))}
              {usageData.sofas.length > 8 && (
                <Typography variant="caption" color="text.secondary">
                  ...и ещё {usageData.sofas.length - 8}
                </Typography>
              )}
            </Alert>
          )}
          <TextField
            fullWidth margin="normal" type="text" label="Количество" placeholder="0"
            value={movementForm.quantity}
            onChange={(e) => setMovementForm({ ...movementForm, quantity: normalizeDecimalInput(e.target.value) })}
            inputProps={{ inputMode: 'decimal', pattern: '[0-9.,]*' }}
            InputProps={{
              endAdornment: selectedFabric && <InputAdornment position="end">{selectedFabric.unit}</InputAdornment>,
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth margin="normal" label="Причина / Комментарий"
            placeholder="Например: Поставка от поставщика №123"
            multiline rows={2} value={movementForm.reason}
            onChange={(e) => setMovementForm({ ...movementForm, reason: e.target.value })}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setMovementDialog(false)} sx={{ borderRadius: 2, textTransform: 'none', color: '#64748b' }}>Отмена</Button>
          <Button onClick={handleSaveMovement} variant="contained"
            sx={{
              borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600,
              background: movementForm.movement_type === 'income'
                ? 'linear-gradient(135deg, #059669 0%, #34d399 100%)'
                : 'linear-gradient(135deg, #d97706 0%, #fbbf24 100%)',
            }}>
            {movementForm.movement_type === 'income' ? 'Добавить приход' : 'Списать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог удаления */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Удалить ткань?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="textSecondary">
            Вы уверены, что хотите удалить ткань <strong>"{selectedFabric?.name}"</strong>? Это действие нельзя отменить.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteDialog(false)} sx={{ borderRadius: 2, textTransform: 'none', color: '#64748b' }}>Отмена</Button>
          <Button onClick={handleDeleteFabric} variant="contained" color="error" sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог истории использования */}
      <Dialog open={usageDialog} onClose={() => setUsageDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <History sx={{ color: '#1e40af' }} />
          История использования — {selectedFabric?.name}
        </DialogTitle>
        <DialogContent>
          {usageLoading ? (
            <Box display="flex" justifyContent="center" py={4}><LinearProgress sx={{ width: '100%' }} /></Box>
          ) : usageData ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                <Typography variant="body2">
                  Текущий остаток: <strong>{usageData.current_quantity} {usageData.unit}</strong>
                </Typography>
              </Alert>

              {usageData.sofas && usageData.sofas.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1e40af' }}>
                    📦 Использована в диванах ({usageData.sofas.length})
                  </Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Диван</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Модель</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Тип ткани</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Расход</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Дата</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {usageData.sofas.map((s, i) => (
                          <TableRow key={i} hover>
                            <TableCell><Chip label={`#${s.sofa_number}`} size="small" /></TableCell>
                            <TableCell>{s.model_name || '—'}</TableCell>
                            <TableCell><Chip label={s.fabric_type} size="small" variant="outlined" color={s.fabric_type === 'основная' ? 'primary' : 'secondary'} /></TableCell>
                            <TableCell align="right"><strong>{s.consumption} {usageData.unit}</strong></TableCell>
                            <TableCell>{s.created_at ? new Date(s.created_at).toLocaleDateString('ru-RU') : '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {usageData.movements && usageData.movements.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1e40af' }}>
                    📋 Все движения ({usageData.movements.length})
                  </Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Тип</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Кол-во</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Остаток после</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Причина</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Дата</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {usageData.movements.map((m) => (
                          <TableRow key={m.id} hover>
                            <TableCell>
                              <Chip
                                label={m.movement_type === 'income' ? 'Приход' : 'Расход'}
                                size="small"
                                color={m.movement_type === 'income' ? 'success' : 'warning'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 600, color: m.movement_type === 'income' ? '#059669' : '#d97706' }}>
                                {m.movement_type === 'income' ? '+' : '-'}{m.quantity} {usageData.unit}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {m.balance_after != null ? `${m.balance_after} ${usageData.unit}` : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {m.reason || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>{m.created_at ? new Date(m.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {(!usageData.sofas || usageData.sofas.length === 0) && (!usageData.movements || usageData.movements.length === 0) && (
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 3 }}>
                  Нет данных об использовании этой ткани
                </Typography>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setUsageDialog(false)} sx={{ borderRadius: 2, textTransform: 'none', color: '#64748b' }}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {/* Уведомления */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
